// src/utils/poUtils.js
// Vendor PO helpers: number generation and PDF rendering.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../services/supabase';
import { savePdfWithDialog } from './saveFile';

/**
 * Controlled list of payment-terms values — keeps data clean for filtering / reporting.
 * Add new options here, not by free-typing on individual POs/vendors.
 */
export const PAYMENT_TERMS_OPTIONS = [
  { value: 'Net 15',          label: 'Net 15' },
  { value: 'Net 30',          label: 'Net 30' },
  { value: 'Net 45',          label: 'Net 45' },
  { value: 'Net 60',          label: 'Net 60' },
  { value: 'Net 75',          label: 'Net 75' },
  { value: 'Net 120',         label: 'Net 120' },
  { value: 'Due on receipt',  label: 'Due on receipt' },
  { value: 'COD',             label: 'COD' },
  { value: 'Prepaid',         label: 'Prepaid' },
];

/**
 * Suggest the next PO number in the form PO-YYYY-NNNN.
 * Resets to 0001 each calendar year. User can override the suggested value
 * on the PO detail form (po_number is free-form, just must be unique).
 */
export const generatePONumber = async () => {
  const year = new Date().getFullYear();
  const prefix = `PO-${year}-`;
  const { data, error } = await supabase
    .from('purchase_orders')
    .select('po_number')
    .like('po_number', `${prefix}%`)
    .order('po_number', { ascending: false })
    .limit(1);
  if (error) throw error;

  let next = 1;
  if (data && data.length > 0) {
    const match = data[0].po_number.match(/^PO-\d{4}-(\d+)$/);
    if (match) next = parseInt(match[1], 10) + 1;
  }
  return `${prefix}${String(next).padStart(4, '0')}`;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Image load failed: ${src}`));
    setTimeout(() => reject(new Error('Image load timeout')), 5000);
    img.src = src;
  });

/**
 * Generate and download a PDF for a purchase order.
 * Layout mirrors the invoice template (same letterhead, colors, table styling)
 * with PO-specific blocks: Vendor (left), Ship-To (right), Line items, Total.
 *
 * @param {object} args
 * @param {object} args.po       - purchase_orders row (incl. po_number, po_date, payment_terms, ship_via, notes_to_vendor)
 * @param {array}  args.lines    - purchase_order_lines rows
 * @param {object} args.supplier - suppliers row (vendor block)
 * @param {object} args.shop     - shops row with ship_to_* fields (Ship-To block)
 */
export const generatePOPDF = async ({ po, lines, supplier, shop }) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const primaryColor = '#384f77';

  // Preload images (so they're ready before autoTable's didDrawPage fires)
  let logoImg = null;
  let miaImg = null;
  try { logoImg = await loadImage(require('../assets/acaLogo.png')); } catch (e) { console.error(e); }
  try { miaImg = await loadImage(require('../assets/madeInAmerica.png')); } catch (e) { console.error(e); }

  // ---- Title ----
  doc.setFontSize(24);
  doc.setTextColor(primaryColor);
  doc.text('PURCHASE ORDER', pageWidth / 2, 14, { align: 'center' });

  // ---- PO number (top-left, above company info) ----
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text(`PO: ${po.po_number || 'N/A'}`, 15, 20);

  // ---- Company info (top-left) ----
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('Advanced Controls & Automation', 15, 30);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text('574 Hudson River Road', 15, 36);
  doc.text('Waterford, NY 12188', 15, 42);
  doc.text('518-369-4408', 15, 48);
  doc.text('sales@acamachine.com', 15, 54);

  // ---- Logo (top-right) ----
  if (logoImg) {
    try {
      doc.addImage(logoImg, 'PNG', pageWidth - 45, 30, 30, 30);
    } catch (err) {
      console.error('addImage logo failed:', err);
    }
  }

  // ---- Vendor block (left) ----
  doc.setFontSize(10);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('Vendor:', 15, 72);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  let vY = 78;
  doc.text(supplier?.name || 'N/A', 15, vY);
  if (supplier?.contact_name) { vY += 6; doc.text(supplier.contact_name, 15, vY); }
  if (supplier?.address) { vY += 6; doc.text(supplier.address, 15, vY); }
  const vendorCSZ = [supplier?.city, supplier?.state].filter(Boolean).join(', ');
  if (vendorCSZ || supplier?.zip) {
    vY += 6;
    doc.text(`${vendorCSZ}${vendorCSZ && supplier?.zip ? ' ' : ''}${supplier?.zip || ''}`, 15, vY);
  }
  if (supplier?.contact_phone) { vY += 6; doc.text(`Phone: ${supplier.contact_phone}`, 15, vY); }
  if (supplier?.contact_email) { vY += 6; doc.text(`Email: ${supplier.contact_email}`, 15, vY); }

  // ---- Ship-To block (right) ----
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('Ship To:', pageWidth / 2 + 5, 72);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  let sY = 78;
  const shipName = shop?.ship_to_name || shop?.name || 'N/A';
  doc.text(shipName, pageWidth / 2 + 5, sY);
  if (shop?.ship_to_contact_name) { sY += 6; doc.text(shop.ship_to_contact_name, pageWidth / 2 + 5, sY); }
  if (shop?.ship_to_address) { sY += 6; doc.text(shop.ship_to_address, pageWidth / 2 + 5, sY); }
  const shipCSZ = [shop?.ship_to_city, shop?.ship_to_state].filter(Boolean).join(', ');
  if (shipCSZ || shop?.ship_to_zip) {
    sY += 6;
    doc.text(`${shipCSZ}${shipCSZ && shop?.ship_to_zip ? ' ' : ''}${shop?.ship_to_zip || ''}`, pageWidth / 2 + 5, sY);
  }
  if (shop?.ship_to_contact_phone) { sY += 6; doc.text(`Phone: ${shop.ship_to_contact_phone}`, pageWidth / 2 + 5, sY); }
  if (shop?.ship_to_contact_email) { sY += 6; doc.text(`Email: ${shop.ship_to_contact_email}`, pageWidth / 2 + 5, sY); }

  // ---- Summary strip: PO Date | Payment Terms | Ship Via ----
  const poDate = po.po_date ? new Date(po.po_date + 'T00:00:00').toLocaleDateString() : 'N/A';
  autoTable(doc, {
    startY: Math.max(vY, sY) + 10,
    head: [['PO DATE', 'PAYMENT TERMS', 'SHIP VIA']],
    body: [[poDate, po.payment_terms || '-', po.ship_via || '-']],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2, textColor: [0, 0, 0] },
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { left: 15, right: 15 },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 60 },
      2: { cellWidth: 'auto' },
    },
  });

  // ---- Line items ----
  const linesData = (lines || []).map((l) => {
    const ext = Number(l.quantity || 0) * Number(l.unit_price || 0);
    return [
      String(l.line_number ?? ''),
      l.description || '-',
      Number(l.quantity || 0).toString(),
      l.uom || 'ea',
      `$${Number(l.unit_price || 0).toFixed(2)}`,
      `$${ext.toFixed(2)}`,
    ];
  });

  let finalY = doc.lastAutoTable.finalY + 5;
  autoTable(doc, {
    startY: finalY,
    head: [['LINE', 'DESCRIPTION', 'QTY', 'UOM', 'UNIT PRICE', 'EXTENDED']],
    body: linesData.length > 0 ? linesData : [['', '(no line items)', '', '', '', '']],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2, textColor: [0, 0, 0] },
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { left: 15, right: 15, bottom: 35 },
    columnStyles: {
      0: { cellWidth: 14 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 14, halign: 'right' },
      3: { cellWidth: 14 },
      4: { cellWidth: 24, halign: 'right' },
      5: { cellWidth: 26, halign: 'right' },
    },
    didDrawPage: () => {
      // Made in America footer on every page
      if (miaImg) {
        try {
          doc.addImage(miaImg, 'PNG', pageWidth - 67.5, pageHeight - 25, 52.5, 15);
        } catch (err) {
          console.error('addImage MIA failed:', err);
        }
      }
    },
  });

  finalY = doc.lastAutoTable.finalY;

  // ---- Total (bottom-right) ----
  const total = (lines || []).reduce(
    (sum, l) => sum + Number(l.quantity || 0) * Number(l.unit_price || 0),
    0
  );
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total: $${total.toFixed(2)}`, pageWidth - 15, finalY + 10, { align: 'right' });

  // ---- Notes to vendor (if any) ----
  if (po.notes_to_vendor) {
    doc.setFontSize(10);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 15, finalY + 20);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(po.notes_to_vendor, 15, finalY + 26, { maxWidth: pageWidth - 30 });
  }

  // ---- Save ----
  const safeFileName = (po.po_number || 'purchase-order').replace(/[^a-z0-9._-]/gi, '_');
  await savePdfWithDialog(doc, `${safeFileName}.pdf`);
};
